/**
 * These utils consist of helper methods for travel rule. Travel rule is triggered
 * when a user is sending more than $3000.00 using PayPal balance or credit card.
 * The travel rule info is contained within the funding option, and generally the
 * user will need to enter a form of government ID and date of birth. The user may
 * also have to enter a non-P.O. Box address. These utils help with checking if
 * travel rule is in effect, which inputs need to be filled out by the user, and
 * validating the date entered into the date of birth inputs.
 *
 * TODO: implement travel rule non-P.O. Box address utils, if needed
 */

import _ from 'lodash'
import { GOVT_ID_TYPES } from '../constants'

/**
 * Method that pulls the contingency data off of the selected funding option
 * @param selectedFundingOption {Object} - the selected funding option
 * @returns Array of Strings - e.g. ['GOVT_ID']
 */
export function calculateAttributesToCollect(selectedFundingOption = {}) {
  const { contingencies = [] } = selectedFundingOption
  const contingencyNeedingDataCollection = _.find(contingencies, {
    action: 'PERFORM_DATA_COLLECTION',
  })
  const attributesToCollect = _.get(
    contingencyNeedingDataCollection,
    'data_collection_details.attributes',
    []
  )
  return attributesToCollect.filter(
    attr => attr === 'GOVT_ID' || attr === 'NON_PO_BOX_ADDRESS'
  )
}

/**
 * Returns whether or not travel rule is in effect for selected funding option
 *
 * TODO: add non-P.O. box address travel rule to this check
 *
 * @param selectedFundingOption {Object} - the selected funding option
 * @returns {Boolean} - true if travel rule is in effect
 */
export function isTravelRule(selectedFundingOption) {
  return calculateAttributesToCollect(selectedFundingOption).length > 0
}

/**
 * Returns whether or not travel rule is in effect and the user needs to input
 * a government ID
 *
 * @param selectedFundingOption {Object} - the selected funding option
 * @returns {Boolean} - true if user needs to input government ID
 */
export function isTravelRuleGovernmentId(selectedFundingOption) {
  return calculateAttributesToCollect(selectedFundingOption).includes('GOVT_ID')
}

/**
 * Returns whether or not travel rule is in effect and the user needs to input
 * a new, non-P.O. box address
 *
 * @param selectedFundingOption {Object} - the selected funding option
 * @returns {Boolean} - true if user needs to input new, non-P.O. box address
 */
export function isTravelRuleAddress(selectedFundingOption) {
  return calculateAttributesToCollect(selectedFundingOption).includes(
    'NON_PO_BOX_ADDRESS'
  )
}

/**
 * Returns whether or not the currently inputted government ID is valid, based
 * on regex rules for each type of government ID
 *
 * @param governmentId {String} - the currently inputted government ID
 * @param selectedGovernmentIdType {String} - the currently selected government ID type
 * @returns {Boolean} - true if government ID passes regex test
 */
export function isGovernmentIdValid(governmentId, selectedGovernmentIdType) {
  const patterns = {
    [GOVT_ID_TYPES.SSN]: /(^\d{3}-?\d{2}-?\d{4}$|^XXX-XX-XXXX$)/i,
    [GOVT_ID_TYPES.ITIN]: /(^\d{2}-?\d{7}$|^XX-XXXXXXX$)/i,
    [GOVT_ID_TYPES.ALIEN]: /(^.*$)/,
    [GOVT_ID_TYPES.PASSPORT]: /(^.*$)/,
  }

  const pattern = patterns[selectedGovernmentIdType]

  if (governmentId.match(pattern)) {
    return true
  }

  return false
}

/**
 * Returns whether or not the currently inputted address is valid, based on
 * whether there is PO Box in either of the first two lines, or if any of the
 * required fields are blank
 *
 * @param newTravelRuleAddress {Object} - the current travel rule address being inputted
 * @param hasPostcodeError {Boolean} - whether or not there is an invalid postcode inputted
 * @returns {Boolean} - true if the address is valid
 */
export function isTravelRuleAddressValid(
  newTravelRuleAddress,
  hasPostcodeError
) {
  if (hasPostcodeError) {
    return false
  }

  const {
    line1 = '',
    line2 = '',
    city = '',
    state = '',
    postcode = '',
  } = newTravelRuleAddress

  // if either of the address lines has PO box-like words, or the city is 'dpo',
  // it is invalid
  if (hasPOBox(line1) || hasPOBox(line2) || isCityPOBox(city)) {
    return false
  }

  return !!(line1 && city && state && postcode)
}

/**
 * Returns whether or not there is likely 'PO Box' or some variation of it
 * somehwere in a string
 *
 * @param str {String} - the string to test
 * @returns {Boolean} - true if the string has PO Box
 */
export function hasPOBox(str = '') {
  const poBoxContainMatches = ['pobox', 'pobx', 'pbox', 'pbx']
  const poBoxExactMatches = ['pob', 'pb', 'po']
  const testString = str.replace(/[^a-zA-Z]/g, '').toLowerCase()
  if (
    poBoxContainMatches.some(poBoxString => testString.includes(poBoxString))
  ) {
    return true
  }
  if (poBoxExactMatches.some(poBoxString => testString === poBoxString)) {
    return true
  }
  return false
}

export function isCityPOBox(city = '') {
  const cityPOBoxes = ['dpo', 'fpo', 'apo']
  return cityPOBoxes.includes(city.toLowerCase())
}

/**
 * Returns whether or not we have all necessary info for travel rule
 *
 * @returns {Boolean} - true if all travel rule info is present and valid
 */
export function hasAllNeededTravelRuleInfo(props) {
  const {
    selectedFundingOption,
    governmentId,
    selectedGovernmentIdType,
    newTravelRuleAddress,
  } = props
  if (
    isTravelRuleGovernmentId(selectedFundingOption) &&
    !isGovernmentIdValid(governmentId, selectedGovernmentIdType)
  ) {
    return false
  }
  if (
    isTravelRuleAddress(selectedFundingOption) &&
    !isTravelRuleAddressValid(newTravelRuleAddress)
  ) {
    return false
  }
  return true
}

/**
 * Massages data from props (react/redux land) into the correct form we need to
 * post to the server
 *
 * @returns {Object} - object to be used to post to the server
 */
export function massageTravelRuleDataForPost(props) {
  const {
    selectedFundingOption,
    governmentId,
    selectedGovernmentIdType,
    newTravelRuleAddress,
    country,
  } = props
  const { line1, line2, city, state, postcode } = newTravelRuleAddress

  let trData = {}

  if (isTravelRuleGovernmentId(selectedFundingOption)) {
    trData = {
      ...trData,
      identifierIssuer: selectedGovernmentIdType,
      identifierValue: governmentId,
    }
  }
  if (isTravelRuleAddress(selectedFundingOption)) {
    trData = {
      ...trData,
      street1: line1,
      street2: line2,
      city,
      state,
      postalCode: postcode,
      countryCode: country,
      errorMessage: 'Please enter your street address (no P.O. boxes)',
    }
  }
  return trData
}
